/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAuthorizationCodeData.ts (Authorization Codes)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for fetching and managing single Authorization Code data.
    Handles loading states and data adaptation to form state.

────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAuthorizationCodeById } from '../api/authorizationCodesApi';
import { FormState } from '../types';
import { getDefaultFormState } from '../utils/formDefaults';

/**
 * Hook for fetching authorization code data
 */
export function useAuthorizationCodeData(
  id: string | undefined,
  duplicateId: string | null,
  tenantSlug: string
) {
  const location = useLocation();
  const [formData, setFormData] = useState<FormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoadedIdRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');

  useEffect(() => {
    const loadId = duplicateId || id;
    const savedDataKey = 'authorizationCodes:saveAndNewData';
    const locationKey = location.key || location.pathname + location.search;

    if (!loadId || loadId === 'new') {
      // Check if we need to reset:
      // 1. ID changed
      // 2. Location key changed (React Router navigation)
      // 3. sessionStorage timestamp indicates reset needed
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = lastLoadedIdRef.current === 'new' ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLoadedIdRef.current !== 'new' || 
                          lastLocationKeyRef.current !== locationKey ||
                          (lastLoadedIdRef.current === 'new' && loadId === 'new' && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        lastLoadedIdRef.current = 'new';
        lastLocationKeyRef.current = locationKey;
        
        // Clear sessionStorage for "Save and New" functionality
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form to default values
        if (!duplicateId) {
          setFormData(getDefaultFormState());
        } else {
          setFormData(null);
        }
      }
      return;
    }

    if (lastLoadedIdRef.current === loadId && lastLocationKeyRef.current === locationKey) {
      return;
    }

    lastLoadedIdRef.current = loadId;
    lastLocationKeyRef.current = locationKey;
    setIsLoading(true);

    fetchAuthorizationCodeById(loadId, tenantSlug)
      .then((record) => {
        const loaded: FormState = {
          code: record.code ?? '',
          alias: record.alias ?? '',
          description: record.description ?? '',
          cosId: record.classOfService?.id ?? record.cosId ?? null,
          active: record.active ?? true
        };

        // If duplicating, clear code and alias
        if (duplicateId) {
          loaded.code = '';
          loaded.alias = '';
        }

        setFormData(loaded);
      })
      .catch(() => {
        lastLoadedIdRef.current = null;
        setFormData(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, duplicateId, tenantSlug, location.key, location.pathname, location.search]);

  return {
    formData,
    isLoading
  };
}

